import { useState } from 'react'
import { RefreshCw } from 'lucide-react'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { CompanyEquityTransactionsRegister } from '@/components/custom/companies/overview/equity-transactions-register'
import { FinancialStatCards } from '@/components/custom/companies/overview/financial-stat-cards'
import { HistoricalValuationChart } from '@/components/custom/companies/overview/historical-valuation-chart'
import { SectionHeader } from '@/components/custom/companies/section-header'
import { useCompanyValuationHistory } from '@/hooks/use-company-valuation-history'
import { formatDate } from '@/lib/format'
import type { Company } from '@/types/api'
import type { ValuationHistoryRange } from '@/services/companies.service'
import { isActualVsBudgetEnabled } from '@/features/actual-vs-budget-production/access'

const VALUATION_RANGES: ValuationHistoryRange[] = ['LTM', 'ALL']

function isValuationRange(v: string): v is ValuationHistoryRange {
  return (VALUATION_RANGES as string[]).includes(v)
}

export function OverviewTab({ company }: { company: Company }) {
  const [valuationRange, setValuationRange] =
    useState<ValuationHistoryRange>('ALL')
  const { data: valuationHistory, isLoading: valuationLoading } =
    useCompanyValuationHistory(company.id, valuationRange)

  const ltmPeriodEndDate = company.financials?.ltmPeriodEndDate ?? null
  const financialsUpdatedAt = company.financialsUpdatedAt ?? null
  const showActualVsBudget = isActualVsBudgetEnabled(import.meta.env)

  return (
    <div className="space-y-4">
      <section className="space-y-3">
        <SectionHeader
          title="Financials"
          afterTitle={
            <Tooltip>
              <TooltipTrigger asChild>
                <Typography
                  as="span"
                  variant="body-sm"
                  className="cursor-default text-primary/70"
                  tabIndex={0}
                >
                  · LTM
                </Typography>
              </TooltipTrigger>
              <TooltipContent>
                Last Twelve Months, measured at each quarter-end
              </TooltipContent>
            </Tooltip>
          }
        />
        {ltmPeriodEndDate || financialsUpdatedAt ? (
          <div className="flex flex-wrap gap-2">
            <div className="flex h-[22px] items-center gap-1 rounded-sm bg-primary/5 px-2 py-0.5 text-primary/70">
              <RefreshCw className="h-3 w-3" />
              <Typography
                variant="body-sm"
                as="span"
                className="text-primary/70"
              >
                As of: {ltmPeriodEndDate ? formatDate(ltmPeriodEndDate) : '—'}
              </Typography>
            </div>
            <div className="flex h-[22px] items-center rounded-sm bg-primary/5 px-2 py-0.5 text-primary/70">
              <Typography
                variant="body-sm"
                as="span"
                className="text-primary/70"
              >
                Last synced:{' '}
                {financialsUpdatedAt ? formatDate(financialsUpdatedAt) : '—'}
              </Typography>
            </div>
          </div>
        ) : null}
        <FinancialStatCards
          companyId={company.id}
          surface="overview"
          showActualVsBudget={showActualVsBudget}
        />
      </section>

      <section className="space-y-3">
        <SectionHeader title="Equity Transactions" />
        <CompanyEquityTransactionsRegister companyId={company.id} />
      </section>

      <section className="space-y-3">
        <SectionHeader
          title="Historical Quarterly Valuation"
          right={
            <Select
              value={valuationRange}
              onValueChange={(v) => {
                if (isValuationRange(v)) setValuationRange(v)
              }}
            >
              <SelectTrigger
                aria-label="Valuation history range"
                variant="ghost"
                className="h-7 w-auto cursor-pointer rounded-sm px-2 py-1 text-sm font-normal leading-5 [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:opacity-60"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end">
                <SelectItem value="LTM">Last 4 Quarters</SelectItem>
                <SelectItem value="ALL">All History</SelectItem>
              </SelectContent>
            </Select>
          }
        />
        <HistoricalValuationChart
          periods={valuationHistory?.periods ?? []}
          isLoading={valuationLoading}
        />
      </section>
    </div>
  )
}
